"use client";

import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useMemo } from "react";
import { showToast, TOAST_TYPES } from "@/lib/toast-utils/toast.util";
import { paymentMethodSchema } from "@/schema/billing/paymentMethodSchema";
import { createPaymentMethod } from "@/services/paymentMethod.service";
import type {
  PaymentMethodFormValues,
  UsePaymentMethodOptions,
} from "@/interface/billing/paymentMethod.types";
import type { ProfileData } from "@/interface/types";
import useProfileStore from "@/store/useProfileStore";
import { useTranslation } from "@/hooks/useTranslation.hook";

export const usePaymentMethod = ({ onSuccess }: UsePaymentMethodOptions) => {
  const { profileData, setProfile } = useProfileStore();

  const { t } = useTranslation();

  const form = useForm<PaymentMethodFormValues>({
    resolver: zodResolver(paymentMethodSchema),
    defaultValues: {
      card_token: "",
      set_as_default: true,
      type: "",
    },
    mode: "onChange",
  });

  const mutation = useMutation({
    mutationKey: ["createPaymentMethod"],
    mutationFn: async (data: PaymentMethodFormValues) => {
      return createPaymentMethod({
        card_token: data.card_token,
        set_as_default: data.set_as_default,
        type: data.type, // Using card_token as type as per requirement
      });
    },
    onSuccess: (data) => {
      showToast(
        TOAST_TYPES.success,
        data.message || t("Payment method added successfully.")
      );
      form.reset({
        card_token: "",
        set_as_default: true,
        type: "",
      });
      setProfile({ ...profileData, has_payment_method: true } as ProfileData);
      onSuccess?.();
    },
  });

  const onSubmit = useCallback(
    (data: PaymentMethodFormValues) => {
      mutation.mutate(data);
    },
    [mutation]
  );

  return useMemo(
    () => ({
      form,
      onSubmit,
      isSaving: mutation.isPending,
      error: mutation.error,
    }),
    [form, onSubmit, mutation.isPending, mutation.error]
  );
};
